﻿import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSession } from "@/auth/session";
import MiPerfilModal from "@/components/feature/MiPerfilModal";
import {
  listNotifications,
  markAllNotificationsRead,
  markNotificationRead,
  type NotificationItem,
} from "@/lib/api";

const routeLabels: Record<string, string> = {
  "/": "Dashboard",
  "/clientes": "Atención al Cliente",
  "/facturacion": "Facturación",
  "/operaciones": "Operaciones",
  "/gastos-variables": "Gastos Variables",
  "/administracion": "Administración",
  "/bodega": "Bodega / Inventario",
  "/reportes": "Reportes",
  "/lecturas-periodo": "Lecturas del Período",
  "/seguimiento-terreno": "Seguimiento Terreno",
  "/notificaciones": "Notificaciones",
};

interface HeaderProps {
  collapsed: boolean;
  onToggle: () => void;
}

export default function Header({ collapsed, onToggle }: HeaderProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { session, logout, profile, hasModule } = useSession();
  const [showNotifications, setShowNotifications] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [showPerfil, setShowPerfil] = useState(false);
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [notificationsLoading, setNotificationsLoading] = useState(false);

  const currentLabel = routeLabels[location.pathname] ?? "Sistema APR";
  const userName = profile?.fullName ?? "Usuario APR";
  const userRole = toRoleLabel(profile?.roles?.[0]);
  const userInitials = getInitials(userName);
  const canAccessSettings = hasModule("configuracion");
  const unreadCount = useMemo(() => notifications.filter((item) => !item.read).length, [notifications]);
  const preview = notifications.slice(0, 6);

  useEffect(() => {
    let cancelled = false;
    if (!session) return;

    async function load(): Promise<void> {
      setNotificationsLoading(true);
      try {
        const result = await listNotifications(session, 20);
        if (!cancelled) setNotifications(result.items);
      } finally {
        if (!cancelled) setNotificationsLoading(false);
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [session]);

  useEffect(() => {
    let cancelled = false;
    if (!session || !showNotifications) return;

    async function refresh(): Promise<void> {
      try {
        const result = await listNotifications(session, 20);
        if (!cancelled) setNotifications(result.items);
      } catch {
        // noop
      }
    }

    void refresh();
    return () => {
      cancelled = true;
    };
  }, [session, showNotifications]);

  async function handleMarkAllRead(): Promise<void> {
    if (!session) return;
    await markAllNotificationsRead(session);
    setNotifications((prev) => prev.map((item) => ({ ...item, read: true })));
  }

  async function handleNotificationClick(item: NotificationItem): Promise<void> {
    if (!session) return;
    if (!item.read) {
      await markNotificationRead(session, item.key);
      setNotifications((prev) => prev.map((row) => (row.key === item.key ? { ...row, read: true } : row)));
    }
    setShowNotifications(false);
    if (item.link) navigate(item.link);
  }

  return (
    <>
      <header
        className={`fixed top-0 right-0 z-20 h-16 app-glass flex items-center px-6 gap-4 transition-all duration-300 ${
          collapsed ? "left-16" : "left-60"
        }`}
      >
      <div className="flex items-center gap-3 flex-1">
        <button
          onClick={onToggle}
          className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-blue-50 transition-colors cursor-pointer text-slate-500"
        >
          <i className={`${collapsed ? "ri-menu-unfold-line" : "ri-menu-fold-line"} text-lg`} />
        </button>
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <span className="text-slate-400">APR</span>
          <i className="ri-arrow-right-s-line text-slate-300" />
          <span className="text-slate-800 font-semibold">{currentLabel}</span>
        </div>
      </div>

      <div className="relative hidden md:block">
        <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-gray-400">
          <i className="ri-search-line text-sm" />
        </div>
        <input
          type="text"
          placeholder="Buscar cliente, boleta, OT..."
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          className="pl-9 pr-4 py-2 text-sm bg-white rounded-xl w-72"
        />
      </div>

      <div className="hidden lg:flex items-center gap-2 px-3 py-1.5 bg-emerald-50 rounded-lg border border-emerald-100">
        <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
        <span className="text-xs text-emerald-700 font-medium whitespace-nowrap">Sistema Activo</span>
      </div>

      <div className="relative">
        <button
          onClick={() => {
            setShowNotifications(!showNotifications);
            setShowUserMenu(false);
          }}
          className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors cursor-pointer relative text-gray-500"
        >
          <i className="ri-notification-3-line text-lg" />
          {unreadCount > 0 ? <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-red-500 rounded-full" /> : null}
        </button>
        {showNotifications && (
          <div className="absolute right-0 top-12 w-80 bg-white rounded-xl border border-gray-100 shadow-lg z-50 overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
              <span className="font-semibold text-gray-800 text-sm">Notificaciones</span>
              <button
                onClick={() => void handleMarkAllRead()}
                className="text-xs text-blue-600 cursor-pointer hover:underline"
              >
                Marcar todas como leídas
              </button>
            </div>
            <div className="max-h-72 overflow-y-auto">
              {notificationsLoading ? (
                <div className="px-4 py-4 text-sm text-gray-500">Cargando...</div>
              ) : preview.length === 0 ? (
                <div className="px-4 py-4 text-sm text-gray-500">Sin notificaciones</div>
              ) : (
                preview.map((item) => (
                  <button
                    type="button"
                    key={item.key}
                    onClick={() => void handleNotificationClick(item)}
                    className="w-full text-left px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-50 flex items-start gap-3"
                  >
                    <div
                      className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${
                        item.type === "error"
                          ? "bg-red-500"
                          : item.type === "warning"
                            ? "bg-amber-500"
                            : item.type === "success"
                              ? "bg-emerald-500"
                              : "bg-blue-500"
                      }`}
                    />
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm leading-snug ${item.read ? "text-gray-600" : "text-gray-800 font-medium"}`}>
                        {item.title}
                      </p>
                      <p className="text-xs text-gray-400 mt-0.5">{toRelativeTime(item.createdAt)}</p>
                    </div>
                  </button>
                ))
              )}
            </div>
            <div className="px-4 py-2.5 text-center">
              <button
                onClick={() => {
                  setShowNotifications(false);
                  navigate("/notificaciones");
                }}
                className="text-xs text-blue-600 cursor-pointer hover:underline"
              >
                Ver todas las notificaciones
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="relative">
        <button
          onClick={() => {
            setShowUserMenu(!showUserMenu);
            setShowNotifications(false);
          }}
          className="flex items-center gap-2.5 px-2 py-1.5 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
        >
          <div className="w-8 h-8 flex items-center justify-center rounded-full bg-blue-600 text-white text-xs font-bold flex-shrink-0">
            {userInitials}
          </div>
          <div className="hidden md:block text-left">
            <div className="text-sm font-semibold text-gray-800 leading-tight whitespace-nowrap">{userName}</div>
            <div className="text-xs text-gray-400 whitespace-nowrap">{userRole}</div>
          </div>
          <div className="w-4 h-4 flex items-center justify-center text-gray-400">
            <i className="ri-arrow-down-s-line text-sm" />
          </div>
        </button>
        {showUserMenu && (
          <div className="absolute right-0 top-12 w-48 bg-white rounded-xl border border-gray-100 shadow-lg z-50 overflow-hidden">
            <div className="py-1">
              <button
                onClick={() => {
                  setShowUserMenu(false);
                  setShowPerfil(true);
                }}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer"
              >
                <div className="w-4 h-4 flex items-center justify-center"><i className="ri-user-line" /></div>
                Mi Perfil
              </button>
              {canAccessSettings ? (
                <button
                  onClick={() => {
                    setShowUserMenu(false);
                    navigate("/configuracion");
                  }}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer"
                >
                  <div className="w-4 h-4 flex items-center justify-center"><i className="ri-settings-3-line" /></div>
                  Configuración
                </button>
              ) : null}
              <div className="border-t border-gray-100 my-1" />
              <button
                onClick={() => {
                  setShowUserMenu(false);
                  logout();
                  navigate("/login");
                }}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 cursor-pointer"
              >
                <div className="w-4 h-4 flex items-center justify-center"><i className="ri-logout-box-line" /></div>
                Cerrar Sesión
              </button>
            </div>
          </div>
        )}
      </div>

      </header>

      {showPerfil && <MiPerfilModal onClose={() => setShowPerfil(false)} />}

      {(showNotifications || showUserMenu) && (
        <div
          className="fixed inset-0 z-10"
          onClick={() => {
            setShowNotifications(false);
            setShowUserMenu(false);
          }}
        />
      )}
    </>
  );
}

function toRelativeTime(isoDate: string): string {
  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) return "";
  const diffMs = Date.now() - date.getTime();
  const diffMin = Math.max(1, Math.round(diffMs / 60000));
  if (diffMin < 60) return `Hace ${diffMin} min`;
  const diffHour = Math.round(diffMin / 60);
  if (diffHour < 24) return `Hace ${diffHour} h`;
  const diffDay = Math.round(diffHour / 24);
  return `Hace ${diffDay} d`;
}

function getInitials(fullName: string): string {
  const initials = fullName
    .split(" ")
    .map((value) => value.trim())
    .filter(Boolean)
    .slice(0, 2)
    .map((value) => value[0]?.toUpperCase() ?? "")
    .join("");
  return initials || "UA";
}

function toRoleLabel(roleCode?: string): string {
  const labels: Record<string, string> = {
    admin: "Administrador",
    operator: "Operador",
    cashier: "Cajero",
    field_reader: "Lector de Terreno",
    readonly: "Solo Lectura",
  };
  if (!roleCode) return "Sin rol";
  return labels[roleCode] ?? roleCode;
}
